import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Loader2, Phone, Mail } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingPagoCompletadoPage = () => {
  const [params] = useSearchParams();
  const bookingId = params.get('booking_id');
  const [status, setStatus] = useState('checking');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!bookingId) {
      setStatus('error');
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/bookings/public/${bookingId}/status`);
        if (res.data.deposit_paid) {
          setData(res.data);
          setStatus('paid');
          return;
        }
      } catch (err) {
        // 404 in the first attempts is expected while the Redsys callback is
        // still being processed. Log only persistent server-side failures.
        if (err?.response?.status && err.response.status >= 500) {
          console.error('Booking status poll failed:', err);
        }
      }
      attempts++;
      if (attempts >= 10) {
        setStatus('pending');
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-halo-cream py-24" data-testid="booking-payment-success">
      <div className="max-w-2xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-halo-surface rounded-2xl p-10 border border-halo-taupe/10 text-center"
        >
          {status === 'checking' && (
            <>
              <Loader2 className="mx-auto text-halo-brown animate-spin mb-4" size={48} />
              <h1 className="font-heading text-2xl text-halo-charcoal mb-2">
                Comprobando tu pago...
              </h1>
              <p className="text-halo-text">Esto puede tardar unos segundos.</p>
            </>
          )}

          {status === 'paid' && data && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={48} className="text-green-600" />
              </div>
              <h1 className="font-heading text-3xl text-halo-charcoal mb-3">
                ¡Pago recibido!
              </h1>
              <p className="text-halo-text mb-6">
                Hemos recibido el depósito de <strong>{data.deposit_amount}€</strong>.
              </p>

              <div className="bg-halo-charcoal text-halo-cream rounded-xl p-6 mb-6">
                <p className="text-halo-cream/60 text-xs tracking-widest mb-2">TU Nº DE CLIENTE</p>
                <p className="font-heading text-3xl tracking-widest mb-3">{data.customer_number}</p>
                <p className="text-halo-cream/80 text-sm">
                  {data.date} a las {data.time}
                </p>
                <p className="text-halo-cream/60 text-xs mt-2">
                  Total: {data.total_price}€ · Pagado: {data.deposit_amount}€
                </p>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-5 text-left mb-6">
                <p className="text-sm text-yellow-900 leading-relaxed">
                  <strong>📞 Tu reserva está PENDIENTE de confirmar.</strong>
                  <br />
                  Te llamaremos para confirmar la fecha y hora reservada. Si tienes prisa,
                  llámanos al{' '}
                  <a href="tel:678901086" className="font-bold underline">
                    678 901 086
                  </a>{' '}
                  indicando tu nº de cliente.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="tel:678901086">
                  <Button className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream w-full sm:w-auto">
                    <Phone size={16} className="mr-2" /> Llamar 678 901 086
                  </Button>
                </a>
                <Link to="/">
                  <Button variant="outline" className="w-full sm:w-auto">
                    Volver al inicio
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <Loader2 className="mx-auto text-halo-brown mb-4" size={48} />
              <h1 className="font-heading text-2xl text-halo-charcoal mb-2">Pago en proceso</h1>
              <p className="text-halo-text mb-6">
                Tu pago aún se está procesando. Te enviaremos un email en cuanto se confirme.
              </p>
              <Link to="/">
                <Button variant="outline">Volver al inicio</Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
              <h1 className="font-heading text-2xl text-halo-charcoal mb-2">
                No hemos podido localizar la reserva
              </h1>
              <Link to="/contacto">
                <Button>
                  <Mail size={16} className="mr-2" /> Contactar
                </Button>
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export const BookingPagoErrorPage = () => (
  <div className="min-h-screen bg-halo-cream py-24" data-testid="booking-payment-error">
    <div className="max-w-2xl mx-auto px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-halo-surface rounded-2xl p-10 border border-halo-taupe/10 text-center"
      >
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle size={48} className="text-red-500" />
        </div>
        <h1 className="font-heading text-3xl text-halo-charcoal mb-3">Pago no completado</h1>
        <p className="text-halo-text mb-6">
          El pago se ha cancelado o ha sido rechazado por el banco. No se ha realizado ningún cargo.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/reservar">
            <Button className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream">
              Probar de nuevo
            </Button>
          </Link>
          <Link to="/contacto">
            <Button variant="outline">Contactar</Button>
          </Link>
        </div>
      </motion.div>
    </div>
  </div>
);
